/**
 * InlineNotaEditor — mini-modal per aggiungere/modificare/eliminare la
 * "Nota importante" di una attività SENZA aprire il form Modifica completo.
 * Si apre dalla riga della tabella attività nel Cronoprogramma.
 */
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";

export default function InlineNotaEditor({ attivita, onClose, qc, cantiereId }) {
  const [testo, setTesto] = React.useState(attivita?.nota_importante || "");
  const [rossa, setRossa] = React.useState(!!attivita?.nota_importante_rossa);

  const save = useMutation({
    mutationFn: (payload) => api.patch(`/cronoprogramma/${attivita.attivita_id}`, payload).then((r) => r.data),
    onSuccess: () => {
      toast.success(testo ? "Nota salvata sull'attività" : "Nota rimossa");
      qc.invalidateQueries({ queryKey: ["attivita-crono", cantiereId] });
      qc.invalidateQueries({ queryKey: ["eventi"] });
      qc.invalidateQueries({ queryKey: ["calendario"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore salvataggio nota"),
  });

  const handleSave = () => save.mutate({
    nota_importante: testo.trim(),
    nota_importante_rossa: testo.trim() ? rossa : false,
  });
  const handleDelete = () => save.mutate({ nota_importante: "", nota_importante_rossa: false });

  const hadNote = !!attivita?.nota_importante;

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="inline-nota-editor"
    >
      <div
        className={`bg-white rounded-md shadow-2xl w-full max-w-md ${rossa ? "border-2 border-rose-400" : "border border-slate-200"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-4 py-3 border-b ${rossa ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest ${rossa ? "text-rose-700" : "text-amber-800"} flex items-center gap-1`}>
            {rossa && <span>⚠️</span>}
            {hadNote ? "Modifica nota importante" : "Aggiungi nota importante"}
          </div>
          <div className="font-display font-bold text-sm mt-0.5 truncate">{attivita?.nome}</div>
        </div>

        <div className="p-4 space-y-3">
          <textarea
            autoFocus
            rows="4"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Es. ATTENDERE ARRIVO MATERIALE · ACCESSO CANTIERE SOLO DALLE 8:00 · CONTATTARE CLIENTE PRIMA"
            data-testid="input-inline-nota"
            className={`w-full px-3 py-2 border rounded-sm text-sm font-semibold ${rossa ? "border-rose-400 text-rose-900 bg-rose-50/40" : "border-amber-300"}`}
          />

          <label className="flex items-start gap-2 cursor-pointer p-2 border border-slate-200 rounded-sm hover:bg-slate-50" data-testid="input-inline-rossa">
            <input
              type="checkbox"
              checked={rossa}
              onChange={(e) => setRossa(e.target.checked)}
              className="w-4 h-4 accent-rose-600 mt-0.5"
            />
            <span className={`text-xs ${rossa ? "text-rose-700 font-bold" : "text-slate-700"}`}>
              ⚠️ Segna come URGENTE (rosso lampeggiante nel Gantt + evento calendario rosso + push con requireInteraction)
            </span>
          </label>

          {testo && (
            <div className={`p-2 rounded-sm border text-xs ${rossa ? "bg-rose-100 border-rose-300 text-rose-900 font-bold" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
              <div className="text-[10px] uppercase font-bold opacity-70 mb-1">Anteprima badge Gantt</div>
              <span className={`inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full text-white text-[10px] font-black ${rossa ? "bg-rose-600 animate-pulse" : "bg-amber-500"}`}>⚠️</span>
              {testo}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-between gap-2">
          <div>
            {hadNote && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={save.isPending}
                data-testid="btn-inline-nota-delete"
                className="px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 border border-rose-300 rounded-sm disabled:opacity-50"
              >
                🗑 Rimuovi nota
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-xs font-semibold border border-slate-300 rounded-sm hover:bg-white"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={save.isPending}
              data-testid="btn-inline-nota-save"
              className={`px-3 py-2 text-xs font-bold text-white rounded-sm disabled:opacity-50 ${rossa ? "bg-rose-600 hover:bg-rose-700" : "bg-orange-600 hover:bg-orange-700"}`}
            >
              {save.isPending ? "…" : "💾 Salva nota"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
